import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { turbineService, type Turbine, type Photo, type Report, type Video } from '@/services/turbineService'
import { authService } from '@/services/authService'
import axios from 'axios'
import { useLanguage } from '@/contexts/LanguageContext'
import { useNotifications } from '@/contexts/NotificationContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import useAppStore from '@/store/appStore'
import { cacheManager, photoImageCache } from '@/utils/cache'
import { getRoomDisplayName, MAX_PHOTOS_PER_UPLOAD } from './helpers'

/**
 * Kismi yukleme uyarisi. Backend `failed: [{name, reason}]` doner; `success:true`
 * gelse bile bu dizi doluysa kullanici KAYIP dosyalari gormelidir.
 */
const partialUploadMessage = (failed: Array<{ name: string; reason: string }>): string =>
  failed.map((f) => `${f.name}: ${f.reason}`).join(' · ')

export const useTurbineDetailViewModel = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useLanguage()
  // Global toast katmani. Onceki lokal `useNotification` tek slotluktu: ardisik iki
  // bildirim birbirini eziyordu ve modal acikken arkada kaliyordu. Cagri imzasi ayni.
  const { showNotification } = useNotifications()
  
  const [turbine, setTurbine] = useState<Turbine | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [_photos, setPhotos] = useState<Record<number, Photo[]>>({})
  const [selectedFiles, setSelectedFiles] = useState<Record<number | string, File[]>>({})
  const [selectedBladeFiles, setSelectedBladeFiles] = useState<Record<string, File[]>>({})
  const [uploading, setUploading] = useState<Record<number | string, boolean>>({})
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)
  const [modalPhoto, setModalPhoto] = useState<{ photos: (Photo | File)[], currentIndex: number, roomNumber: number } | null>(null)
  const [allPhotos, setAllPhotos] = useState<Photo[]>([])
  const [categoryPhotos, setCategoryPhotos] = useState<{drone: Photo[], operator: Photo[], robot: Photo[]}>({
    drone: [],
    operator: [],
    robot: []
  })
  const [searchTerm, setSearchTerm] = useState('')

  // Category search terms
  const [droneSearchTerm, _setDroneSearchTerm] = useState('')
  const [operatorSearchTerm, _setOperatorSearchTerm] = useState('')
  const [robotSearchTerm, _setRobotSearchTerm] = useState('')

  // Bulk delete states
  const [selectedPhotosForBulkDelete, setSelectedPhotosForBulkDelete] = useState<Set<string>>(new Set())
  const [isBulkDeleteMode, setIsBulkDeleteMode] = useState(false)
  const [isDeletingBulk, setIsDeletingBulk] = useState(false)

  // Category comparison modes
  const [isDroneComparisonMode, setIsDroneComparisonMode] = useState(false)
  const [isOperatorComparisonMode, setIsOperatorComparisonMode] = useState(false)
  const [isRobotComparisonMode, setIsRobotComparisonMode] = useState(false)
  
  // Category selected photos for comparison
  const [selectedDronePhotosForComparison, setSelectedDronePhotosForComparison] = useState<Photo[]>([])
  const [selectedOperatorPhotosForComparison, setSelectedOperatorPhotosForComparison] = useState<Photo[]>([])
  const [selectedRobotPhotosForComparison, setSelectedRobotPhotosForComparison] = useState<Photo[]>([])
  
  // Category photo years
  const [dronePhotoYear, setDronePhotoYear] = useState<number>(new Date().getFullYear()) // Default to current year
  const [operatorPhotoYear, setOperatorPhotoYear] = useState<number>(new Date().getFullYear()) // Default to current year
  const [robotPhotoYear, setRobotPhotoYear] = useState<number>(new Date().getFullYear()) // Default to current year
  
  // Blade selection for photo uploads
  const [selectedDroneBlade, setSelectedDroneBlade] = useState<string>('A')
  const [selectedOperatorBlade, setSelectedOperatorBlade] = useState<string>('A')
  const [selectedRobotBlade, setSelectedRobotBlade] = useState<string>('A')
  
  // Blade dropdown states
  const [isDroneBladeDropdownOpen, setIsDroneBladeDropdownOpen] = useState(false)
  const [isOperatorBladeDropdownOpen, setIsOperatorBladeDropdownOpen] = useState(false)
  const [isRobotBladeDropdownOpen, setIsRobotBladeDropdownOpen] = useState(false)
  
  // Filtered photos for each category - using allPhotos like blade photos for consistent instant updates
  const _filteredDronePhotos = allPhotos.filter(photo => {
    const isCategory = photo.category === 'drone'
    const matchesSearch = photo.originalName?.toLowerCase().includes(droneSearchTerm.toLowerCase()) ||
                         photo.uploader_name?.toLowerCase().includes(droneSearchTerm.toLowerCase()) ||
                         photo.uploader_email?.toLowerCase().includes(droneSearchTerm.toLowerCase())
    const matchesYear = dronePhotoYear === 0 || photo.year === dronePhotoYear
    return isCategory && matchesSearch && matchesYear
  })
  
  const _filteredOperatorPhotos = allPhotos.filter(photo => {
    const isCategory = photo.category === 'operator'
    const matchesSearch = photo.originalName?.toLowerCase().includes(operatorSearchTerm.toLowerCase()) ||
                         photo.uploader_name?.toLowerCase().includes(operatorSearchTerm.toLowerCase()) ||
                         photo.uploader_email?.toLowerCase().includes(operatorSearchTerm.toLowerCase())
    const matchesYear = operatorPhotoYear === 0 || photo.year === operatorPhotoYear
    return isCategory && matchesSearch && matchesYear
  })
  
  const _filteredRobotPhotos = allPhotos.filter(photo => {
    const isCategory = photo.category === 'robot'
    const matchesSearch = photo.originalName?.toLowerCase().includes(robotSearchTerm.toLowerCase()) ||
                         photo.uploader_name?.toLowerCase().includes(robotSearchTerm.toLowerCase()) ||
                         photo.uploader_email?.toLowerCase().includes(robotSearchTerm.toLowerCase())
    const matchesYear = robotPhotoYear === 0 || photo.year === robotPhotoYear
    return isCategory && matchesSearch && matchesYear
  })
  const [photosLoading, setPhotosLoading] = useState(false)
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; photo: Photo | null }>({ isOpen: false, photo: null })
  const [showRoomEditModal, setShowRoomEditModal] = useState(false)
  const [newRoomCount, setNewRoomCount] = useState(0)
  const [roomUpdateLoading, setRoomUpdateLoading] = useState(false)

  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear()) // For room photos
  const [allPhotosSelectedYear, setAllPhotosSelectedYear] = useState<number>(0) // 0 means "all years"
  const [selectedPhotoType, setSelectedPhotoType] = useState<string>('all') // For type filtering
  const [availableYears, setAvailableYears] = useState<number[]>([])
  // --- Yukleme hatalari (Kural 4: hata != bos veri) ---
  // Bir yukleme basarisiz olursa mevcut veri KORUNUR; ilgili bolumde
  // "yuklenemedi + tekrar dene" bandi cikar. Boylece "kayit yok" ile
  // "yuklenemedi" gorsel olarak ayrisir.
  const [photosLoadError, setPhotosLoadError] = useState<string | null>(null)
  const [reportsLoadError, setReportsLoadError] = useState<string | null>(null)
  const [videosLoadError, setVideosLoadError] = useState<string | null>(null)
  // Video listesi bir effect icinde cekiliyor; "tekrar dene" bu sayaci artirip effect'i yeniden tetikler.
  const [videosReloadToken, setVideosReloadToken] = useState(0)
  // Silme modali icindeki hata (Kural 7: yikici islem hata verdiyse modal KAPANMAZ)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false)
  // Toplu silmede basarisiz olanlarin adlari + nedenleri (toast'a sigmaz, banda yazilir)
  const [bulkDeleteError, setBulkDeleteError] = useState<string | null>(null)
  
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false)
  const [isAllPhotosYearDropdownOpen, setIsAllPhotosYearDropdownOpen] = useState(false)
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false)
  
  // PDF Report states
  const [selectedReports, setSelectedReports] = useState<File[]>([])
  const [uploadingReports, setUploadingReports] = useState(false)
  const [reportUploadError, setReportUploadError] = useState<string | null>(null)
  const [_showReportDetails, _setShowReportDetails] = useState(false)
  const [existingReports, setExistingReports] = useState<Report[]>([])
  const [loadingReports, setLoadingReports] = useState(false)
  const [reportYear, setReportYear] = useState<number>(new Date().getFullYear())
  const [reportWeek, setReportWeek] = useState<number>(0) // 0 means all weeks
  const [isReportYearDropdownOpen, setIsReportYearDropdownOpen] = useState(false)
  const [isReportWeekDropdownOpen, setIsReportWeekDropdownOpen] = useState(false)
  const yearDropdownRef = useRef<HTMLDivElement>(null)
  const reportWeekDropdownRef = useRef<HTMLDivElement>(null)

  // Video upload states
  const [selectedVideoType, setSelectedVideoType] = useState<string>('blade-a')
  const [selectedVideos, setSelectedVideos] = useState<File[]>([])
  const [uploadingVideos, setUploadingVideos] = useState(false)
  const [videoUploadError, setVideoUploadError] = useState<string | null>(null)
  const [videoYear, setVideoYear] = useState<number>(new Date().getFullYear())
  const [videoWeek, setVideoWeek] = useState<number>(0) // 0 means all weeks
  const [existingVideos, setExistingVideos] = useState<Video[]>([])
  const [loadingVideos, setLoadingVideos] = useState(false)
  const [isVideoTypeDropdownOpen, setIsVideoTypeDropdownOpen] = useState(false)
  const [isVideoYearDropdownOpen, setIsVideoYearDropdownOpen] = useState(false)
  const [isVideoWeekDropdownOpen, setIsVideoWeekDropdownOpen] = useState(false)
  const [playingVideoId, setPlayingVideoId] = useState<number | null>(null)
  const videoTypeDropdownRef = useRef<HTMLDivElement>(null)
  const videoYearDropdownRef = useRef<HTMLDivElement>(null)
  const videoWeekDropdownRef = useRef<HTMLDivElement>(null)

  const allPhotosYearDropdownRef = useRef<HTMLDivElement>(null)
  const allPhotosWeekDropdownRef = useRef<HTMLDivElement>(null)
  const typeDropdownRef = useRef<HTMLDivElement>(null)
  const _droneYearDropdownRef = useRef<HTMLDivElement>(null)
  const _operatorYearDropdownRef = useRef<HTMLDivElement>(null)
  const _robotYearDropdownRef = useRef<HTMLDivElement>(null)

  // Week state for All Photos
  const [allPhotosSelectedWeek, setAllPhotosSelectedWeek] = useState<number>(0) // 0 means all weeks
  const [isAllPhotosWeekDropdownOpen, setIsAllPhotosWeekDropdownOpen] = useState(false)

  // Generate week options (1-52)
  const weekOptions = Array.from({ length: 52 }, (_, i) => i + 1)
  
  // Photo comparison states
  const [isComparisonMode, setIsComparisonMode] = useState(false)
  const [selectedPhotosForComparison, setSelectedPhotosForComparison] = useState<Photo[]>([])
  const [comparisonPhotos, setComparisonPhotos] = useState<Photo[]>([])
  const [showComparisonModal, setShowComparisonModal] = useState(false)
  
  // Comments sidebar state
  const [isCommentsOpen, setIsCommentsOpen] = useState(false)
  
  // Category year dropdown states
  const [isDroneYearDropdownOpen, setIsDroneYearDropdownOpen] = useState(false)
  const [isOperatorYearDropdownOpen, setIsOperatorYearDropdownOpen] = useState(false)
  const [isRobotYearDropdownOpen, setIsRobotYearDropdownOpen] = useState(false)
  
  // Blade room selection states - her blade için hangi room'a yükleme yapılacağı
  const [bladeRoomSelection, setBladeRoomSelection] = useState<{[bladeIndex: number]: number}>({
    0: 1, // Blade A -> Room 1 (default)
    1: 2, // Blade B -> Room 2 (default)
    2: 3  // Blade C -> Room 3 (default)
  })
  const [isBladeRoomDropdownOpen, setIsBladeRoomDropdownOpen] = useState<{[bladeIndex: number]: boolean}>({
    0: false,
    1: false,
    2: false
  })
  
  // Check if user is admin or company manager
  const currentUser = authService.getCurrentUser()
  const triggerTurbineRefresh = useAppStore(state => state.triggerTurbineRefresh)
  const isAdmin = currentUser?.role === 'admin'
  const isSuperAdmin = currentUser?.role === 'superadmin'
  const isCompanyManager = currentUser?.role === 'company_manager'
  const isCustomer = currentUser?.role === 'customer'
  
  // Debug logging to check user role (remove in production)
  // console.log('Current User Role:', currentUser?.role, 'Is Customer:', isCustomer)
  const canDeletePhotos = isAdmin || isCompanyManager || isSuperAdmin

  // Clear cache for this turbine (useful when Wasabi data is empty but cache shows data)
  const clearTurbineCache = () => {
    if (!turbine) return
    
    // Clear photo cache
    cacheManager.clearByPattern(`photos_${turbine.id}`)
    photoImageCache.clearCache()
    
    // Reload photos
    loadAllPhotos(true)
    
    showNotification('info', 'Cache Temizlendi', 'Fotoğraf cache\'i temizlendi ve veriler yeniden yüklendi.')
  }

  // Force clear cache when user role changes to ensure proper visibility
  useEffect(() => {
    if (currentUser?.role) {
      // Clear cache to ensure proper role-based visibility
      clearTurbineCache()
    }
  }, [currentUser?.role])

  // ESC key listener for closing modals
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (modalPhoto) {
          closeModal()
        } else if (deleteModal.isOpen) {
          // Kural 7: silme istegi ucustayken ESC kapatmaz, aksi halde kullanici
          // modal icindeki hata mesajini goremeden dialog kaybolur.
          if (!isDeletingPhoto) {
            setDeleteModal({ isOpen: false, photo: null })
            setDeleteError(null)
          }
        } else if (showRoomEditModal) {
          setShowRoomEditModal(false)
        } else if (isYearDropdownOpen) {
          setIsYearDropdownOpen(false)
        } else if (isAllPhotosYearDropdownOpen) {
          setIsAllPhotosYearDropdownOpen(false)
        } else if (isAllPhotosWeekDropdownOpen) {
          setIsAllPhotosWeekDropdownOpen(false)
        } else if (isTypeDropdownOpen) {
          setIsTypeDropdownOpen(false)
        } else if (isReportYearDropdownOpen) {
          setIsReportYearDropdownOpen(false)
        } else if (isReportWeekDropdownOpen) {
          setIsReportWeekDropdownOpen(false)
        } else if (showComparisonModal) {
          setShowComparisonModal(false)
        } else if (isCommentsOpen) {
          setIsCommentsOpen(false)
        } else if (isDroneBladeDropdownOpen) {
          setIsDroneBladeDropdownOpen(false)
        } else if (isOperatorBladeDropdownOpen) {
          setIsOperatorBladeDropdownOpen(false)
        } else if (isRobotBladeDropdownOpen) {
          setIsRobotBladeDropdownOpen(false)
        } else if (isVideoTypeDropdownOpen) {
          setIsVideoTypeDropdownOpen(false)
        } else if (isVideoYearDropdownOpen) {
          setIsVideoYearDropdownOpen(false)
        } else if (isVideoWeekDropdownOpen) {
          setIsVideoWeekDropdownOpen(false)
        }
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [modalPhoto, deleteModal.isOpen, isDeletingPhoto, showRoomEditModal, isYearDropdownOpen, isAllPhotosYearDropdownOpen, isAllPhotosWeekDropdownOpen, isTypeDropdownOpen, isReportYearDropdownOpen, isReportWeekDropdownOpen, showComparisonModal, isCommentsOpen, isDroneBladeDropdownOpen, isOperatorBladeDropdownOpen, isRobotBladeDropdownOpen, isVideoTypeDropdownOpen, isVideoYearDropdownOpen, isVideoWeekDropdownOpen])

  // Close year and week dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (yearDropdownRef.current && !yearDropdownRef.current.contains(event.target as Node)) {
        setIsYearDropdownOpen(false)
      }
      if (allPhotosYearDropdownRef.current && !allPhotosYearDropdownRef.current.contains(event.target as Node)) {
        setIsAllPhotosYearDropdownOpen(false)
      }
      if (allPhotosWeekDropdownRef.current && !allPhotosWeekDropdownRef.current.contains(event.target as Node)) {
        setIsAllPhotosWeekDropdownOpen(false)
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setIsTypeDropdownOpen(false)
      }
      if (reportWeekDropdownRef.current && !reportWeekDropdownRef.current.contains(event.target as Node)) {
        setIsReportWeekDropdownOpen(false)
      }
      if (isReportYearDropdownOpen) {
        setIsReportYearDropdownOpen(false)
      }
      if (videoTypeDropdownRef.current && !videoTypeDropdownRef.current.contains(event.target as Node)) {
        setIsVideoTypeDropdownOpen(false)
      }
      if (videoYearDropdownRef.current && !videoYearDropdownRef.current.contains(event.target as Node)) {
        setIsVideoYearDropdownOpen(false)
      }
      if (videoWeekDropdownRef.current && !videoWeekDropdownRef.current.contains(event.target as Node)) {
        setIsVideoWeekDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch videos when filters change
  useEffect(() => {
    const fetchVideos = async () => {
      if (!turbine) return

      setLoadingVideos(true)
      try {
        const yearParam = videoYear || undefined
        const weekParam = videoWeek && videoWeek !== 0 ? videoWeek : undefined
        const typeParam = selectedVideoType || undefined

        const response = await turbineService.getVideos(turbine.id, yearParam, weekParam, typeParam)

        if (response.success) {
          setExistingVideos(response.data ?? [])
          setVideosLoadError(null)
        } else {
          // Kural 5: success:false bir hatadir. Kural 4: liste [] yapilmaz, mevcut veri korunur.
          setVideosLoadError(responseErrorMessage(response, t, t.errors.loadFailed))
        }
      } catch (error) {
        logError('TurbineDetail.fetchVideos', error)
        setVideosLoadError(toUserMessage(error, t, t.errors.loadFailed))
      } finally {
        setLoadingVideos(false)
      }
    }

    fetchVideos()
  }, [turbine?.id, videoYear, videoWeek, selectedVideoType, videosReloadToken])

  // OPTIMIZED: Load all photos with single API call
  const loadAllPhotos = async (forceRefresh = false, year?: number, week?: number, turbineId?: number) => {
    const targetTurbineId = turbineId || turbine?.id
    if (!targetTurbineId) return

    setPhotosLoading(true)
    try {
      // Single API call with view=all parameter
      const yearParam = year && year !== 0 ? year : undefined
      const weekParam = week && week !== 0 ? week : undefined
      const allResponse = await turbineService.getAllPhotos(targetTurbineId, yearParam, weekParam, forceRefresh)
      
      if (allResponse.success && allResponse.data) {
        const allPhotosFromAPI = allResponse.data
        
        // Extract available years from response
        const allYears = new Set<number>()
        allPhotosFromAPI.forEach((photo: any) => {
          if (photo.year) {
            allYears.add(photo.year)
          } else {
            allYears.add(new Date().getFullYear())
        }
        })
        
        const yearsArray = Array.from(allYears).sort((a, b) => b - a)
        setAvailableYears(yearsArray)
        // Separate blade/room photos from category photos
        const bladeRoomPhotos = allPhotosFromAPI.filter((photo: any) => 
          photo.room && 
          (!photo.category || photo.category === null || photo.category === undefined)
        )
        

        
        // Group category photos
        const dronePhotos = allPhotosFromAPI.filter((photo: any) => photo.category === 'drone')
        const operatorPhotos = allPhotosFromAPI.filter((photo: any) => photo.category === 'operator')
        const robotPhotos = allPhotosFromAPI.filter((photo: any) => photo.category === 'robot')
        
        // Set allPhotos to include ALL photos (blade/room + category) for unified filtering
        setAllPhotos(allPhotosFromAPI)
        setCategoryPhotos({
          drone: dronePhotos,
          operator: operatorPhotos,
          robot: robotPhotos
        })
        
        setPhotosLoadError(null)
      } else {
        // Kural 4: hata != bos veri. Onceden burada liste [] yapiliyordu; fotograflar
        // yuklenemedigi halde kullanici "hic fotograf yok" goruyordu. Artik mevcut veri
        // KORUNUR ve bolumde "yuklenemedi + tekrar dene" bandi cikar.
        setPhotosLoadError(responseErrorMessage(allResponse, t, t.errors.loadFailed))
      }
    } catch (error) {
      logError('TurbineDetail.loadAllPhotos', error)
      setPhotosLoadError(toUserMessage(error, t, t.errors.loadFailed))
    } finally {
      setPhotosLoading(false)
    }
  }

  // Filter photos based on search term and type (year filtering is already done in loadAllPhotos)
  const filteredPhotosBySearch = allPhotos.filter(photo => {
    // Search term filter
    const matchesSearch = (photo.originalName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (photo.room || '').toString().includes(searchTerm)
    
    // Type filter
    let matchesType = true
    if (selectedPhotoType !== 'all') {
      if (selectedPhotoType === 'blade') {
        matchesType = Boolean(photo.blade && photo.blade !== null && photo.blade !== undefined && photo.blade !== '')
      } else if (selectedPhotoType === 'drone') {
        matchesType = photo.category === 'drone'
      } else if (selectedPhotoType === 'operator') {
        matchesType = photo.category === 'operator'
      } else if (selectedPhotoType === 'robot') {
        matchesType = photo.category === 'robot'
      }
    }
    
    return matchesSearch && matchesType
  })

  // Handle delete photo from list
  const handleDeletePhotoFromList = (photo: Photo) => {
    setDeleteModal({ isOpen: true, photo })
  }

  // Handle room count update
  const handleRoomCountUpdate = async () => {
    if (!turbine || newRoomCount < 1 || newRoomCount > 20) return
    
    setRoomUpdateLoading(true)
    
    try {
      const response = await turbineService.updateTurbine(turbine.id, {
        city: turbine.city,
        code: turbine.code,
        rooms: newRoomCount,
        location: turbine.location,
        status: turbine.status,
        company_id: turbine.company_id
      })
      
      if (response.success && response.turbine) {
        setTurbine(response.turbine)
        showNotification('success', 'Oda Sayısı Güncellendi', response.message || 'Oda sayısı başarıyla güncellendi')
        
        // Trigger turbine refresh in dashboard
        triggerTurbineRefresh()
        
        // Handle room count changes
        if (newRoomCount !== turbine.rooms) {
        if (newRoomCount < turbine.rooms) {
            // Rooms decreased: clear photos for deleted rooms
          setPhotos(prev => {
            const updated = { ...prev }
            for (let room = newRoomCount + 1; room <= turbine.rooms; room++) {
              delete updated[room]
            }
            return updated
          })
          } else {
            // Rooms increased: add empty arrays for new rooms
            setPhotos(prev => {
              const updated = { ...prev }
              for (let room = turbine.rooms + 1; room <= newRoomCount; room++) {
                updated[room] = []
              }
              return updated
            })
          }
          
          // Reload all photos to ensure consistency
          loadAllPhotos(true)
        }
        
        setShowRoomEditModal(false)
      } else {
        // Kural 5+6: success:false bir hatadir ve backend mesaji ezilmez.
        showNotification('error', 'Güncelleme Hatası', responseErrorMessage(response, t, 'Oda sayısı güncellenemedi'))
      }
    } catch (error) {
      logError('TurbineDetail.handleRoomCountUpdate', error)
      showNotification('error', t.errors.title, toUserMessage(error, t, 'Oda sayısı güncellenirken bir hata oluştu'))
    } finally {
      setRoomUpdateLoading(false)
    }
  }

  // Handle photo rename


  // Confirm and delete photo
  const confirmDeletePhoto = async () => {
    if (!turbine || !deleteModal.photo) return
    if (isDeletingPhoto) return // Kural 10: cift gonderim engeli

    setIsDeletingPhoto(true)
    setDeleteError(null)

    try {
      const response = await turbineService.deletePhoto(turbine.id, deleteModal.photo.key)
      
      if (response.success) {
        // Remove from allPhotos list
        setAllPhotos(prev => prev.filter(p => p.key !== deleteModal.photo?.key))
        
        // ADDED: Remove from room-specific photos as well
        setPhotos(prev => {
          const updatedPhotos = { ...prev }
          Object.keys(updatedPhotos).forEach(roomKey => {
            const roomNum = parseInt(roomKey)
            if (updatedPhotos[roomNum]) {
              updatedPhotos[roomNum] = updatedPhotos[roomNum].filter(p => p.key !== deleteModal.photo?.key)
            }
          })
          return updatedPhotos
        })
        
        // Update modal photos if modal is open
        if (modalPhoto) {
          const updatedPhotos = modalPhoto.photos.filter(photo => 
            !('key' in photo) || photo.key !== deleteModal.photo?.key
          )
          
          if (updatedPhotos.length === 0) {
            // No more photos, close modal
            setModalPhoto(null)
          } else {
            // Adjust current index if needed
            let newIndex = modalPhoto.currentIndex
            if (newIndex >= updatedPhotos.length) {
              newIndex = updatedPhotos.length - 1
            }
            
            setModalPhoto({
              ...modalPhoto,
              photos: updatedPhotos,
              currentIndex: newIndex
            })
          }
        }
        
        // Show success notification
        showNotification('success', 'Fotoğraf Silindi', 'Fotoğraf başarıyla silindi.')
        
        // Reload turbine data
        if (id) {
          const turbineResponse = await turbineService.getTurbine(parseInt(id))
          if (turbineResponse.success && turbineResponse.turbine) {
            setTurbine(turbineResponse.turbine)
          } else {
            // Asil islem (silme) BASARILI oldu; bu yalnizca ekrandaki turbin
            // ozetini tazeleme cagrisi. Kullaniciya ayri bir hata gostermiyoruz --
            // yaptigi is olmus durumda ve gorebilecegi tek fark sayacin bayat
            // kalmasi. Yine de sessiz gecmiyoruz: kayit log'a dusuyor.
            logError('TurbineDetail.reloadTurbineAfterDelete', turbineResponse.message)
          }
        }

        // Reload reports after successful photo deletion
        await loadReports();

        // Modal YALNIZCA basarida kapanir.
        setDeleteModal({ isOpen: false, photo: null })
      } else {
        // Kural 7: yikici islem hata verdiyse modal KAPANMAZ, hata modalin icinde durur.
        setDeleteError(responseErrorMessage(response, t, 'Fotoğraf silinemedi'))
      }
    } catch (error) {
      logError('TurbineDetail.confirmDeletePhoto', error)
      setDeleteError(toUserMessage(error, t, 'Fotoğraf silinemedi'))
    } finally {
      setIsDeletingPhoto(false)
    }
  }

  // Silme modalini kapatir. Istek ucustayken kapatmaz: kullanici sonucu goremez.
  const closeDeleteModal = () => {
    if (isDeletingPhoto) return
    setDeleteModal({ isOpen: false, photo: null })
    setDeleteError(null)
  }

  // Delete photo function (from modal)
  const handleDeletePhoto = () => {
    if (!modalPhoto) return

    const currentPhoto = modalPhoto.photos[modalPhoto.currentIndex]
    if (!currentPhoto || !('key' in currentPhoto)) return

    // Convert to Photo type for delete modal
    const photoToDelete = currentPhoto as Photo
    setDeleteModal({ isOpen: true, photo: photoToDelete })
  }

  // Bulk delete functions
  const toggleBulkDeleteMode = () => {
    setIsBulkDeleteMode(!isBulkDeleteMode)
    if (isBulkDeleteMode) {
      setSelectedPhotosForBulkDelete(new Set())
    }
  }

  const togglePhotoForBulkDelete = (photoKey: string) => {
    setSelectedPhotosForBulkDelete(prev => {
      const newSet = new Set(prev)
      if (newSet.has(photoKey)) {
        newSet.delete(photoKey)
      } else {
        newSet.add(photoKey)
      }
      return newSet
    })
  }

  const handleBulkDelete = async () => {
    if (selectedPhotosForBulkDelete.size === 0 || !turbine) return

    const confirmed = window.confirm(`${selectedPhotosForBulkDelete.size} fotoğrafı silmek istediğinizden emin misiniz?`)
    if (!confirmed) return

    setIsDeletingBulk(true)
    setBulkDeleteError(null)

    try {
      const deletedKeys = new Set<string>()
      // Sadece sayac yetmez: hangi fotografin NEDEN silinemedigi kullaniciya soylenmeli.
      const failures: string[] = []

      const photoLabel = (photoKey: string): string => {
        const photo = allPhotos.find(p => p.key === photoKey)
        return photo?.originalName || photoKey.split('/').pop() || photoKey
      }

      for (const photoKey of selectedPhotosForBulkDelete) {
        try {
          const response = await turbineService.deletePhoto(turbine.id, photoKey)
          if (response.success) {
            deletedKeys.add(photoKey)
          } else {
            failures.push(`${photoLabel(photoKey)}: ${responseErrorMessage(response, t, 'Fotoğraf silinemedi')}`)
          }
        } catch (error) {
          logError('TurbineDetail.handleBulkDelete', error)
          failures.push(`${photoLabel(photoKey)}: ${toUserMessage(error, t, 'Fotoğraf silinemedi')}`)
        }
      }

      // Yalnizca GERCEKTEN silinenler listeden dusurulur. Silinemeyeni ekrandan
      // kaldirmak kullaniciya "silindi" yalani soylerdi.
      setAllPhotos(prev => prev.filter(p => deletedKeys.has(p.key) === false))

      // Remove from room-specific photos
      setPhotos(prev => {
        const updatedPhotos = { ...prev }
        Object.keys(updatedPhotos).forEach(roomKey => {
          const roomNum = parseInt(roomKey)
          if (updatedPhotos[roomNum]) {
            updatedPhotos[roomNum] = updatedPhotos[roomNum].filter(p => deletedKeys.has(p.key) === false)
          }
        })
        return updatedPhotos
      })

      // Clear selection
      setSelectedPhotosForBulkDelete(new Set())
      setIsBulkDeleteMode(false)

      // Show result notification
      if (failures.length === 0) {
        showNotification('success', 'Fotoğraflar Silindi', `${deletedKeys.size} fotoğraf başarıyla silindi.`)
      } else {
        showNotification('warning', 'Kısmi Başarı', `${deletedKeys.size} fotoğraf silindi, ${failures.length} fotoğraf silinemedi.`)
        // Ayrintili liste toast'a sigmaz; bolum icindeki banda yazilir.
        const shown = failures.slice(0, 8)
        const rest = failures.length - shown.length
        // TODO(i18n): "... fotoğraf daha silinemedi." icin strings.ts'te anahtar yok.
        setBulkDeleteError(shown.join(' • ') + (rest > 0 ? ` • +${rest} fotoğraf daha silinemedi.` : ''))
      }

      // Reload turbine data and reports
      if (id) {
        const turbineResponse = await turbineService.getTurbine(parseInt(id))
        if (turbineResponse.success && turbineResponse.turbine) {
          setTurbine(turbineResponse.turbine)
        } else {
          // Toplu silme BASARILI; bu yalnizca turbin ozetini tazeleme cagrisi.
          // Ayri hata gostermek gurultu olurdu, ama sessiz de gecmiyoruz.
          logError('TurbineDetail.reloadTurbineAfterBulkDelete', turbineResponse.message)
        }
      }
      await loadReports()

    } catch (error) {
      logError('TurbineDetail.handleBulkDelete', error)
      const message = toUserMessage(error, t, 'Fotoğraflar silinirken bir hata oluştu')
      setBulkDeleteError(message)
      showNotification('error', t.errors.title, message)
    } finally {
      setIsDeletingBulk(false)
    }
  }

  // Photo comparison functions
  const toggleComparisonMode = () => {
    setIsComparisonMode(!isComparisonMode)
    if (isComparisonMode) {
      setSelectedPhotosForComparison([])
    }
  }
  
  // Category comparison toggle functions
  const toggleDroneComparisonMode = () => {
    setIsDroneComparisonMode(!isDroneComparisonMode)
    if (isDroneComparisonMode) {
      setSelectedDronePhotosForComparison([])
    }
  }
  
  const toggleOperatorComparisonMode = () => {
    setIsOperatorComparisonMode(!isOperatorComparisonMode)
    if (isOperatorComparisonMode) {
      setSelectedOperatorPhotosForComparison([])
    }
  }
  
  const toggleRobotComparisonMode = () => {
    setIsRobotComparisonMode(!isRobotComparisonMode)
    if (isRobotComparisonMode) {
      setSelectedRobotPhotosForComparison([])
    }
  }

  const togglePhotoSelection = (photo: Photo) => {
    setSelectedPhotosForComparison(prev => {
      const isSelected = prev.some(p => p.key === photo.key)
      if (isSelected) {
        return prev.filter(p => p.key !== photo.key)
      } else if (prev.length < 2) {
        return [...prev, photo]
      }
      return prev
    })
  }

  const openComparisonModal = () => {
    if (selectedPhotosForComparison.length === 2) {
      setShowComparisonModal(true)
    }
  }
  
  // Category photo selection toggle functions
  const toggleDronePhotoSelection = (photo: Photo) => {
    setSelectedDronePhotosForComparison(prev => {
      const isSelected = prev.some(p => p.key === photo.key)
      if (isSelected) {
        return prev.filter(p => p.key !== photo.key)
      } else if (prev.length < 2) {
        return [...prev, photo]
      }
      return prev
    })
  }
  
  const toggleOperatorPhotoSelection = (photo: Photo) => {
    setSelectedOperatorPhotosForComparison(prev => {
      const isSelected = prev.some(p => p.key === photo.key)
      if (isSelected) {
        return prev.filter(p => p.key !== photo.key)
      } else if (prev.length < 2) {
        return [...prev, photo]
      }
      return prev
    })
  }
  
  const toggleRobotPhotoSelection = (photo: Photo) => {
    setSelectedRobotPhotosForComparison(prev => {
      const isSelected = prev.some(p => p.key === photo.key)
      if (isSelected) {
        return prev.filter(p => p.key !== photo.key)
      } else if (prev.length < 2) {
        return [...prev, photo]
      }
      return prev
    })
  }
  
  // Category comparison modal open functions
  const openDroneComparisonModal = () => {
    if (selectedDronePhotosForComparison.length === 2) {
      setComparisonPhotos(selectedDronePhotosForComparison)
      setShowComparisonModal(true)
    }
  }
  
  const openOperatorComparisonModal = () => {
    if (selectedOperatorPhotosForComparison.length === 2) {
      setComparisonPhotos(selectedOperatorPhotosForComparison)
      setShowComparisonModal(true)
    }
  }
  
  const openRobotComparisonModal = () => {
    if (selectedRobotPhotosForComparison.length === 2) {
      setComparisonPhotos(selectedRobotPhotosForComparison)
      setShowComparisonModal(true)
    }
  }

  // Optimized parallel data loading for better performance
  useEffect(() => {
    if (id) {
      const turbineId = parseInt(id)
      
      // Load all data in parallel for faster page loading
      Promise.all([
        loadTurbine(turbineId),
        loadAllPhotos(true, 0, 0, turbineId), // Pass turbineId to avoid waiting for turbine state
        loadReports(reportYear, reportWeek, turbineId) // Pass turbineId to avoid waiting for turbine state
      ]).catch(error => {
        // Sessiz BIRAKILIYOR (bilerek): ucu de kendi try/catch'ine sahip ve hatasini
        // `error` / `photosLoadError` / `reportsLoadError` uzerinden zaten yuzeye
        // cikariyor. Buraya ancak beklenmedik bir programlama hatasi duser.
        logError('TurbineDetail.initialLoad', error)
      })
    }
  }, [id])

  // Reload all photos when year or week filter changes (but not on initial load)
  useEffect(() => {
    if (turbine) {
      loadAllPhotos(false, allPhotosSelectedYear, allPhotosSelectedWeek)
    }
  }, [allPhotosSelectedYear, allPhotosSelectedWeek])

  // Load reports when report year or week changes (but not on initial load since we load it in parallel above)
  useEffect(() => {
    if (turbine) {
      loadReports()
    }
  }, [reportYear, reportWeek])

  // Clean filename function - remove timestamp prefix
  const cleanFileName = (filename: string) => {
    // Remove timestamp prefix like "1754000902264-0-"
    const match = filename.match(/^\d+-\d+-(.+)$/)
    return match ? match[1] : filename
  }

  // Delete report function
  const deleteReport = async (reportId: number) => {
    if (!turbine) return
    
    try {
      const response = await turbineService.deleteReport(turbine.id, reportId)
      
      if (response.success) {
        showNotification('success', 'Rapor başarıyla silindi')
        
        // Immediately remove the deleted report from state for instant UI update
        setExistingReports(prev => prev.filter(report => report.id !== reportId));
      } else {
        // Kural 5+6: success:false bir hatadir, backend mesaji korunur.
        showNotification('error', t.errors.title, responseErrorMessage(response, t, 'Rapor silinirken hata oluştu'))
      }
    } catch (error) {
      logError('TurbineDetail.deleteReport', error)
      showNotification('error', t.errors.title, toUserMessage(error, t, 'Rapor silinirken hata oluştu'))
    }
  }

  // Load reports function
  const loadReports = async (year?: number, week?: number, turbineId?: number) => {
    const targetTurbineId = turbineId || turbine?.id
    const targetYear = year || reportYear
    const targetWeek = week !== undefined ? week : reportWeek
    if (!targetTurbineId) return

    setLoadingReports(true)
    try {
      // Pass week as undefined if it's 0 (all weeks)
      const response = await turbineService.getReports(
        targetTurbineId,
        targetYear,
        targetWeek === 0 ? undefined : targetWeek
      )

      if (response.success) {
        setExistingReports(Array.isArray(response.data) ? response.data : [])
        setReportsLoadError(null)
      } else {
        // Kural 4: "yuklenemedi" ile "rapor yok" ayni sey degil. Mevcut liste korunur.
        setReportsLoadError(responseErrorMessage(response, t, t.errors.loadFailed))
      }
    } catch (error) {
      logError('TurbineDetail.loadReports', error)
      setReportsLoadError(toUserMessage(error, t, t.errors.loadFailed))
    } finally {
      setLoadingReports(false)
    }
  }

  // --- "Tekrar dene" aksiyonlari (ErrorBanner onRetry) ---
  const retryLoadPhotos = () => {
    setPhotosLoadError(null)
    loadAllPhotos(true, allPhotosSelectedYear, allPhotosSelectedWeek)
  }

  const retryLoadReports = () => {
    setReportsLoadError(null)
    loadReports()
  }

  const retryLoadVideos = () => {
    setVideosLoadError(null)
    setVideosReloadToken(token => token + 1)
  }

  const loadTurbine = async (turbineId: number) => {
    
    setLoading(true)
    setError(null)
    
    try {
      const response = await turbineService.getTurbine(turbineId)
      
      if (response.success && response.turbine) {
        setTurbine(response.turbine)
        
        // OPTIMIZED: Load all photos with single API call
        try {
          const allPhotosResponse = await turbineService.getAllPhotos(turbineId)
          if (allPhotosResponse.success && allPhotosResponse.data) {
                        // Group photos by room for room-specific display
            const photosByRoom: Record<number, Photo[]> = {}
            
            allPhotosResponse.data.forEach((photo, index) => {
              // Daha güvenli room parsing
              let room = 1 // default
              if (photo.room !== null && photo.room !== undefined) {
                const roomStr = photo.room.toString().trim()
                const parsedRoom = parseInt(roomStr, 10)
                if (!isNaN(parsedRoom) && parsedRoom > 0) {
                  room = parsedRoom
                }
              }
              
              
              if (!photosByRoom[room]) {
                photosByRoom[room] = []
              }
              photosByRoom[room]!.push(photo)
            })
            
            setPhotos(photosByRoom)
            }
          } catch (photoError) {
          // Sessiz BIRAKILIYOR (bilerek): bu cagri yalnizca odaya gore gruplanmis
          // IKINCIL kopyayi (`_photos`) doldurur. Kullaniciya gosterilen birincil
          // liste `loadAllPhotos`tan gelir ve onun hatasi `photosLoadError` bandinda
          // zaten yuzeye cikar. Ayni hata icin ikinci bir uyari gostermek gurultu olur.
          logError('TurbineDetail.loadTurbine.photos', photoError)
          }
      } else {
        setError(responseErrorMessage(response, t, 'Türbin bulunamadı'))
      }
    } catch (error) {
      logError('TurbineDetail.loadTurbine', error)
      setError(toUserMessage(error, t, 'Türbin bilgileri yüklenirken hata oluştu'))
    } finally {
      setLoading(false)
    }
  }

  // Load photos for all rooms
  useEffect(() => {
    const loadPhotos = async () => {
      if (!turbine) return
      
      try {
        const response = await turbineService.getPhotos(turbine.id)
        if (response.success && response.data) {
          // Group photos by room
          const photosByRoom: { [key: number]: Photo[] } = {}
          response.data.forEach(photo => {
            const room = parseInt(photo.room || '1')
            if (!photosByRoom[room]) {
              photosByRoom[room] = []
            }
            photosByRoom[room].push(photo)
          })
          setPhotos(photosByRoom)
        }
      } catch (error) {
        // Sessiz BIRAKILIYOR (bilerek): `loadAllPhotos` ile ayni veriyi ceken ikincil
        // bir istek. Kullaniciya gorunen hata `photosLoadError` bandindan gelir.
        logError('TurbineDetail.loadPhotosByRoom', error)
      }
    }

    loadPhotos()
  }, [turbine])
  
  // Direct navigation functions
  const goBackToDashboard = () => {
    navigate('/dashboard')
  }
  
  const logout = async () => {
    try {
      // Backend'e logout request gönder + localStorage temizle
      await authService.logout()
      navigate('/')
    } catch (error) {
      // Sessiz BIRAKILIYOR (bilerek): `authService.logout()` backend istegi basarisiz
      // olsa da yerel token/kullanici bilgisini temizler ve kullanici zaten disari
      // aliniyor. Cikis ekraninda gosterilecek bir hata, tamamlanmis bir islemi
      // basarisiz gibi gosterirdi.
      logError('TurbineDetail.logout', error)
      navigate('/')
    }
  }

  // Dosya seçme işlemi - With filename validation
  const handleFileSelect = (roomIndexOrCategory: number | string, newFiles: File[]) => {
    const roomNumber = roomIndexOrCategory

    // Filter files with valid filenames
    const validFilenameRegex = /^[a-zA-Z0-9._-]+$/;
    const validFiles = newFiles.filter(file => {
      const isValid = validFilenameRegex.test(file.name);
      if (!isValid) {
        console.warn(`⚠️ Skipping file with invalid name: ${file.name}`);
      }
      return isValid;
    });

    const invalidFiles = newFiles.filter(file => !validFilenameRegex.test(file.name));

    if (invalidFiles.length > 0) {
      showNotification(
        'error',
        'Geçersiz Dosya Adları',
        `${invalidFiles.length} dosya geçersiz karakter içeriyor ve atlandı.`
      );
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => {
        const currentCount = prev[roomNumber]?.length || 0
        const totalCount = currentCount + validFiles.length

        if (totalCount > MAX_PHOTOS_PER_UPLOAD) {
          const availableSlots = MAX_PHOTOS_PER_UPLOAD - currentCount
          const filesToAdd = validFiles.slice(0, availableSlots)

          if (availableSlots > 0) {
            showNotification(
              'warning',
              'Dosya Limiti',
              `Tek seferde maksimum ${MAX_PHOTOS_PER_UPLOAD} fotoğraf yükleyebilirsiniz. ${filesToAdd.length} fotoğraf seçildi.`
            );

            return {
              ...prev,
              [roomNumber]: [...(prev[roomNumber] || []), ...filesToAdd]
            }
          } else {
            showNotification(
              'warning',
              'Maksimum Limit',
              `Maksimum ${MAX_PHOTOS_PER_UPLOAD} fotoğraf limitine ulaştınız. Daha fazla fotoğraf ekleyemezsiniz.`
            );
            return prev
          }
        }

        return {
          ...prev,
          [roomNumber]: [...(prev[roomNumber] || []), ...validFiles]
        }
      })
    }
  }

  // Seçilen dosyayı kaldırma
  const removeSelectedFile = (roomNumberOrCategory: number | string, fileIndex: number) => {
    setSelectedFiles(prev => ({
      ...prev,
      [roomNumberOrCategory]: prev[roomNumberOrCategory]?.filter((_, i) => i !== fileIndex) || []
    }))
  }

  // Blade-specific file selection functions
  const handleBladeFileSelect = (bladeKey: string, files: File[]) => {
    const validFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/')
      const isPCD = file.name.toLowerCase().endsWith('.pcd')
      return isImage || isPCD
    })

    const invalidFiles = files.filter(file => {
      const isImage = file.type.startsWith('image/')
      const isPCD = file.name.toLowerCase().endsWith('.pcd')
      return !isImage && !isPCD
    })

    if (invalidFiles.length > 0) {
      showNotification(
        'error',
        'Geçersiz Dosya Tipi',
        `${invalidFiles.length} dosya geçersiz formatta ve atlandı. Sadece resim veya .pcd dosyaları yükleyebilirsiniz.`
      );
    }

    if (validFiles.length > 0) {
      setSelectedBladeFiles(prev => {
        const currentCount = prev[bladeKey]?.length || 0
        const totalCount = currentCount + validFiles.length

        if (totalCount > MAX_PHOTOS_PER_UPLOAD) {
          const availableSlots = MAX_PHOTOS_PER_UPLOAD - currentCount
          const filesToAdd = validFiles.slice(0, availableSlots)

          if (availableSlots > 0) {
            showNotification(
              'warning',
              'Dosya Limiti',
              `Tek seferde maksimum ${MAX_PHOTOS_PER_UPLOAD} fotoğraf yükleyebilirsiniz. ${filesToAdd.length} fotoğraf seçildi.`
            );

            return {
              ...prev,
              [bladeKey]: [...(prev[bladeKey] || []), ...filesToAdd]
            }
          } else {
            showNotification(
              'warning',
              'Maksimum Limit',
              `Maksimum ${MAX_PHOTOS_PER_UPLOAD} fotoğraf limitine ulaştınız. Daha fazla fotoğraf ekleyemezsiniz.`
            );
            return prev
          }
        }

        return {
          ...prev,
          [bladeKey]: [...(prev[bladeKey] || []), ...validFiles]
        }
      })
    }
  }

  const removeSelectedBladeFile = (bladeKey: string, fileIndex: number) => {
    setSelectedBladeFiles(prev => ({
      ...prev,
      [bladeKey]: prev[bladeKey]?.filter((_, i) => i !== fileIndex) || []
    }))
  }

  // Blade-specific upload function
  const handleUploadSelectedBlade = async (bladeKey: string, roomNumber: number) => {
    const filesToUpload = selectedBladeFiles[bladeKey] || []
    if (filesToUpload.length === 0) return

    const turbineId = turbine?.id
    if (!turbineId) return

    setUploading(prev => ({ ...prev, [bladeKey]: true }))
    setUploadError(null)

    try {
      const allUploadedPhotos: any[] = [];
      const bladeType = bladeKey.split('-')[1]; // Extract 'A', 'B', etc.

      // Process each file individually to apply the correct logic
      for (const file of filesToUpload) {
        const fileBatch = [file]; // Process one file at a time
        const batchSizeMB = (file.size / 1024 / 1024).toFixed(1);
        console.log(`--- Blade ${bladeType} Upload ---`);
        console.log(`[DEBUG] Starting upload for ${file.name}, size: ${batchSizeMB}MB`);
        
        try {
          // Always use direct upload to avoid Vercel bandwidth limits
          console.log(`[RESULT] Blade Upload: USING DIRECT UPLOAD (optimized for Vercel)`);

          const response = await turbineService.uploadPhotosDirectly(turbineId, roomNumber, fileBatch, new Date().getFullYear(), bladeType);

          if (response.success && response.data) {
            allUploadedPhotos.push(...response.data);
            if (response.failed && response.failed.length > 0) {
              showNotification(
                'warning',
                t.errors.partialUploadTitle,
                partialUploadMessage(response.failed)
              );
            }
          } else {
            // Kural 5+6: success:false bir hatadir. Eskiden `throw new Error(...)`
            // yapiliyordu ve backend mesaji asagidaki jenerik catch'te kayboluyordu.
            showNotification('error', 'Yükleme Başarısız', `${file.name}: ${responseErrorMessage(response, t, 'Yükleme başarısız!')}`);
          }
        } catch (fileError: unknown) {
          // No automatic retry. Just log and notify.
          logError('TurbineDetail.handleUploadSelectedBlade', fileError);
          // Kural 9: ham `error.message` kullaniciya dokulmez.
          showNotification('error', 'Yükleme Başarısız', `${file.name}: ${toUserMessage(fileError, t, 'Yükleme başarısız!')}`);
        }
      }

      // Update UI with all newly uploaded photos
      if (allUploadedPhotos.length > 0) {
        setAllPhotos(prev => [...prev, ...allUploadedPhotos].sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()));
        setPhotos(prev => {
          const updatedPhotos = { ...prev };
          if (!updatedPhotos[roomNumber]) {
            updatedPhotos[roomNumber] = [];
          }
          updatedPhotos[roomNumber] = [...updatedPhotos[roomNumber], ...allUploadedPhotos];
          return updatedPhotos;
        });
      }

      // Clear the selected blade files
      setSelectedBladeFiles(prev => ({
        ...prev,
        [bladeKey]: []
      }))

      // Trigger refresh
      const triggerRefresh = useAppStore.getState().triggerTurbineRefresh
      triggerRefresh()

      // Clear caches for fresh image loading
      photoImageCache.clearCache()
      
      // Hicbir dosya yuklenemediyse YESIL "0 fotoğraf başarıyla yüklendi!" gosterilmez;
      // kullanici tek tek hata bildirimlerini zaten gordu (Kural 8).
      if (allUploadedPhotos.length > 0) {
        showNotification(
          'success',
          `${allUploadedPhotos.length} fotoğraf başarıyla yüklendi!`,
          `Blade ${bladeType} Fotoğrafları - ${getRoomDisplayName(roomNumber)}`
        )
      }

    } catch (error: unknown) {
      logError('TurbineDetail.handleUploadSelectedBlade', error)
      const message = toUserMessage(error, t, 'Yükleme başarısız!')
      setUploadError(message)
      showNotification('error', 'Yükleme başarısız!', message)
    } finally {
      setUploading(prev => ({ ...prev, [bladeKey]: false }))
    }
  }

  // BATCH UPLOAD SYSTEM - Split large uploads into smaller chunks
  const handleUploadSelected = async (roomNumberOrCategory: number | string) => {
    const filesToUpload = selectedFiles[roomNumberOrCategory] || []
    if (filesToUpload.length === 0) return

    
    setUploading({ ...uploading, [roomNumberOrCategory]: true });
    setUploadError(null);
    
    const BATCH_SIZE = 1; // Upload 1 file at a time
    const PHOTO_SIZE_LIMIT = 4 * 1024 * 1024; // 4MB for photos, any larger uses direct upload
    const VIDEO_SIZE_LIMIT = 50 * 1024 * 1024 * 1024; // 50GB for videos
    
    try {
      const allUploadedPhotos: any[] = [];
      
      // Split files into smart batches (by count and size)
      const batches: File[][] = [];
      let currentBatch: File[] = [];
      let currentBatchSize = 0;
      
      for (const file of filesToUpload) {
        const fileSize = file.size;
        
        // If adding this file would exceed batch limits, start new batch
        if (currentBatch.length >= BATCH_SIZE || currentBatchSize + fileSize > PHOTO_SIZE_LIMIT) {
          if (currentBatch.length > 0) {
            batches.push(currentBatch);
            currentBatch = [];
            currentBatchSize = 0;
          }
        }
        
        currentBatch.push(file);
        currentBatchSize += fileSize;
      }
      
      // Add remaining files as final batch
      if (currentBatch.length > 0) {
        batches.push(currentBatch);
      }
      
      // Extra safety check for large batches
      const problematicBatches = batches.filter(batch => 
        batch.length > BATCH_SIZE
      );
      if (problematicBatches.length > 0) {
        console.warn('⚠️ Some batches exceed limits:', problematicBatches);
      }
      
      // Upload each batch sequentially
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        if (!batch || batch.length === 0) continue;
        
        
        // Determine which year to use based on room or category
        let yearToUse = selectedYear;
        if (typeof roomNumberOrCategory === 'string') {
          // It's a category, use the appropriate category year
          if (roomNumberOrCategory === 'drone') yearToUse = dronePhotoYear;
          else if (roomNumberOrCategory === 'operator') yearToUse = operatorPhotoYear;
          else if (roomNumberOrCategory === 'robot') yearToUse = robotPhotoYear;
        }
        
        // Determine blade for category uploads
        let bladeToUse = undefined;
        if (typeof roomNumberOrCategory === 'string') {
          if (roomNumberOrCategory === 'drone') bladeToUse = selectedDroneBlade;
          else if (roomNumberOrCategory === 'operator') bladeToUse = selectedOperatorBlade;
          else if (roomNumberOrCategory === 'robot') bladeToUse = selectedRobotBlade;
        }
        
        const batchSizeMB = (batch.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(1);
        console.log(`--- BATCH ${i + 1}/${batches.length} ---`);
        console.log(`[DEBUG] Starting upload for batch with ${batch.length} file(s), total size: ${batchSizeMB}MB`);
        console.log(`[DEBUG] PHOTO_SIZE_LIMIT is ${(PHOTO_SIZE_LIMIT / 1024 / 1024)}MB`);
        
        try {
          // Validate file sizes before attempting upload.
          // Eskiden burada `throw` ediliyordu; hata jenerik catch'e dusuyor ve
          // "Video dosyası çok büyük" bilgisi kullaniciya ulasmiyordu.
          let oversizedMessage: string | null = null;
          for (const file of batch) {
            const fileSizeMB = (file.size / 1024 / 1024).toFixed(2);
            console.log(`[DEBUG] Checking file: ${file.name} - Size: ${fileSizeMB}MB`);

            if (file.type.startsWith('video/') && file.size > VIDEO_SIZE_LIMIT) {
              const videoSizeGB = (file.size / 1024 / 1024 / 1024).toFixed(2);
              const limitGB = (VIDEO_SIZE_LIMIT / 1024 / 1024 / 1024).toFixed(0);
              console.error(`[ERROR] Video file ${file.name} (${videoSizeGB}GB) exceeds the ${limitGB}GB limit.`);
              oversizedMessage = `Video dosyası çok büyük (>50GB): ${file.name}`;
              break;
            }
          }
          if (oversizedMessage) {
            showNotification('error', `Batch ${i + 1} Başarısız`, oversizedMessage);
            continue;
          }

          // Always use direct upload to avoid Vercel bandwidth limits
          console.log(`[RESULT] USING DIRECT UPLOAD (optimized for Vercel)`);

          const response = await turbineService.uploadPhotosDirectly(turbine?.id || 0, roomNumberOrCategory, batch, yearToUse, bladeToUse);

          if (response.success && response.data) {
            allUploadedPhotos.push(...response.data);

            if (response.failed && response.failed.length > 0) {
              // Kismi kayip BASARI degildir: yesil yerine sari uyari.
              showNotification(
                'warning',
                t.errors.partialUploadTitle,
                partialUploadMessage(response.failed)
              );
            } else {
              showNotification(
                'success',
                `Batch ${i + 1}/${batches.length} Tamamlandı`,
                `${batch.length} dosya başarıyla yüklendi (${batchSizeMB}MB) - Direkt Yükleme`
              );
            }
          } else {
            // Kural 5+6: success:false bir hatadir; backend mesaji Ingilizce bir
            // "Batch N failed: ..." sarmalayicisiyla ezilmez.
            showNotification(
              'error',
              `Batch ${i + 1} Başarısız`,
              responseErrorMessage(response, t, 'Toplu yükleme sırasında hata oluştu.')
            );
          }
        } catch (batchError: unknown) {
          logError('TurbineDetail.handleUploadSelected', batchError);
          // Kural 9: ham hata metni degil, gosterilebilir metin.
          showNotification(
            'error',
            `Batch ${i + 1} Başarısız`,
            toUserMessage(batchError, t, 'Toplu yükleme sırasında hata oluştu.')
          );
        }
        
        // Small delay between batches to be nice to the server
        if (i < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      
      // Update UI with all uploaded photos - this handles both room and category uploads
      setAllPhotos(prev => [...prev, ...allUploadedPhotos].sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()));

      // Also update categoryPhotos state for any category uploads
      setCategoryPhotos(prev => {
        const updated = { ...prev }
        allUploadedPhotos.forEach(photo => {
          if (photo.category === 'drone') {
            updated.drone = [...updated.drone, photo]
          } else if (photo.category === 'operator') {
            updated.operator = [...updated.operator, photo]
          } else if (photo.category === 'robot') {
            updated.robot = [...updated.robot, photo]
          }
        })
        return updated
      });

      setPhotos(prev => {
        const updatedPhotos = { ...prev };
        allUploadedPhotos.forEach(photo => {
          const roomNum = photo.room ? parseInt(photo.room.toString(), 10) : 0;
          if (roomNum) {
            if (!updatedPhotos[roomNum]) {
              updatedPhotos[roomNum] = [];
            }
            updatedPhotos[roomNum]!.push(photo);
          }
        });
        return updatedPhotos;
      });

      // Clear the selected files
        setSelectedFiles(prev => ({
          ...prev,
          [roomNumberOrCategory]: []
      }));

      // Hicbir dosya yuklenemediyse YESIL "0 adet fotoğraf yüklendi" gosterilmez:
      // kullanici batch basina hata bildirimlerini zaten gordu (Kural 8).
      if (allUploadedPhotos.length > 0) {
        // For category uploads (drone, operator, robot), show success notification
        if (typeof roomNumberOrCategory === 'string') {
          // Show success notification with category name
          const categoryNames = {
            'drone': 'Drone',
            'operator': 'Operatör', 
            'robot': 'Robot'
          };
          const categoryName = categoryNames[roomNumberOrCategory as keyof typeof categoryNames] || roomNumberOrCategory;
          showNotification('success', 'Fotoğraf Yüklendi', `${allUploadedPhotos.length} adet ${categoryName} fotoğrafı başarıyla yüklendi.`);
        } else {
          // For room photos, show regular notification
          showNotification('success', 'Fotoğraf Yüklendi', `${allUploadedPhotos.length} adet fotoğraf başarıyla yüklendi.`);
        }
      }

      // Reload reports after successful photo upload - photos are already updated via state
      await loadReports();
        
    } catch (error: unknown) {
      logError('TurbineDetail.handleUploadSelected', error);
      const errorMessage = toUserMessage(error, t, 'Toplu yükleme sırasında hata oluştu.');
      setUploadError(errorMessage);
      
      // Show error notification
      showNotification('error', 'Yükleme Hatası', errorMessage);
    } finally {
      setUploading({ ...uploading, [roomNumberOrCategory]: false });
    }
  };

  // PDF Report handling functions
  const handleReportSelect = (files: File[]) => {
    const pdfFiles = files.filter(file => file.type === 'application/pdf');
    const invalidTypeFiles = files.filter(file => file.type !== 'application/pdf');
    
    // Validate PDF filenames (same as photo validation)
    const validFilenameRegex = /^[a-zA-Z0-9._-]+$/;
    const validPdfFiles = pdfFiles.filter(file => {
      const isValidName = validFilenameRegex.test(file.name);
      if (!isValidName) {
        console.warn(`⚠️ Skipping PDF with invalid name: ${file.name}`);
      }
      return isValidName;
    });
    
    const invalidNameFiles = pdfFiles.filter(file => !validFilenameRegex.test(file.name));
    
    // Show error messages
    let errorMessages = [];
    
    if (invalidTypeFiles.length > 0) {
      errorMessages.push(`${invalidTypeFiles.length} dosya PDF formatında değil`);
    }
    
    if (invalidNameFiles.length > 0) {
      errorMessages.push(`${invalidNameFiles.length} PDF dosyası geçersiz isim içeriyor (Türkçe karakter, boşluk vb. kullanmayın)`);
    }
    
    if (errorMessages.length > 0) {
      setReportUploadError(errorMessages.join(' ve ') + '. Bu dosyalar atlandı.');
      setTimeout(() => setReportUploadError(null), 8000);
    }
    
    if (validPdfFiles.length > 0) {
      setSelectedReports(prev => [...prev, ...validPdfFiles]);
    }
  };

  const removeSelectedReport = (index: number) => {
    setSelectedReports(prev => prev.filter((_, i) => i !== index));
  };

  const uploadReports = async () => {
    if (selectedReports.length === 0 || !turbine) return;

    setUploadingReports(true);
    setReportUploadError(null);

    try {
      
      const formData = new FormData();
      formData.append('year', reportYear.toString());
      
      selectedReports.forEach((file) => {
        formData.append('reports', file);
      });
      
      const token = localStorage.getItem('deepwind-auth-token');
      
      const response = await axios.post(
        `/api/turbines/${turbine.id}/reports`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (response.data.success) {
        setSelectedReports([]);
        
        // Immediately add new reports to the existing state for instant UI update
        if (response.data.data && Array.isArray(response.data.data)) {
          setExistingReports(prev => [...prev, ...response.data.data]);
        }
        
        const failedReports = Array.isArray(response.data.failed) ? response.data.failed : [];
        if (failedReports.length > 0) {
          // 5 PDF'ten 4'u dusse bile eskiden YESIL basari bildirimi cikiyordu.
          showNotification('warning', t.errors.partialUploadTitle, partialUploadMessage(failedReports));
        } else {
          showNotification('success', `${Array.isArray(response.data.data) ? response.data.data.length : selectedReports.length} rapor başarıyla yüklendi.`);
        }
      } else {
        // Kural 5+6: success:false bir hatadir; backend mesaji throw sarmalayicisinda kaybolmaz.
        const message = responseErrorMessage(response.data, t, 'Rapor yükleme başarısız');
        setReportUploadError(message);
        showNotification('error', t.errors.title, message);
      }
      
    } catch (error: unknown) {
      logError('TurbineDetail.uploadReports', error);
      const errorMessage = toUserMessage(error, t, 'Rapor yükleme sırasında hata oluştu.');
      setReportUploadError(errorMessage);
      
      // Show error notification  
      showNotification('error', t.errors.title, errorMessage);
    } finally {
      setUploadingReports(false);
    }
  };

  // Video upload handlers
  const handleVideoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])

    // Validate video files
    const ACCEPTED_VIDEO_TYPES = [
      'video/mp4',
      'video/quicktime',
      'video/x-msvideo',
      'video/webm',
      'video/x-matroska'
    ]

    const MAX_VIDEO_SIZE = 500 * 1024 * 1024 // 500 MB

    const validFiles: File[] = []
    const errorMessages: string[] = []

    files.forEach(file => {
      // Check file type
      if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
        errorMessages.push(`${file.name}: Geçersiz format`)
        return
      }

      // Check file size
      if (file.size > MAX_VIDEO_SIZE) {
        errorMessages.push(`${file.name}: Dosya çok büyük (maks 500MB)`)
        return
      }

      // Check filename
      const invalidChars = /[^A-Z0-9_\-.]/i.test(file.name)
      if (invalidChars) {
        errorMessages.push(`${file.name}: Geçersiz dosya adı`)
        return
      }

      validFiles.push(file)
    })

    if (errorMessages.length > 0) {
      setVideoUploadError(errorMessages.join(', '))
      setTimeout(() => setVideoUploadError(null), 8000)
    }

    if (validFiles.length > 0) {
      setSelectedVideos(prev => [...prev, ...validFiles])
    }

    // Reset input
    event.target.value = ''
  }

  const removeSelectedVideo = (index: number) => {
    setSelectedVideos(prev => prev.filter((_, i) => i !== index))
  }

  const uploadVideos = async () => {
    if (selectedVideos.length === 0 || !turbine) return

    setUploadingVideos(true)
    setVideoUploadError(null)

    try {
      const response = await turbineService.uploadVideosDirectly(
        turbine.id,
        selectedVideoType,
        selectedVideos,
        videoYear
      )

      if (response.success) {
        setSelectedVideos([])

        // Add new videos to existing state
        if (response.data && Array.isArray(response.data)) {
          setExistingVideos(prev => [...response.data!, ...prev])
        }

        if (response.failed && response.failed.length > 0) {
          // Kismi kayip BASARI degildir: yesil yerine sari uyari + kayip dosyalar.
          showNotification('warning', t.errors.partialUploadTitle, partialUploadMessage(response.failed))
        } else {
          showNotification('success', `${selectedVideos.length} video başarıyla yüklendi.`)
        }
      } else {
        // Kural 5+6: success:false bir hatadir; backend mesaji korunur.
        const message = responseErrorMessage(response, t, 'Video yükleme başarısız')
        setVideoUploadError(message)
        showNotification('error', t.errors.title, message)
      }
    } catch (error: unknown) {
      logError('TurbineDetail.uploadVideos', error)
      const errorMessage = toUserMessage(error, t, 'Video yükleme sırasında hata oluştu.')
      setVideoUploadError(errorMessage)
      showNotification('error', t.errors.title, errorMessage)
    } finally {
      setUploadingVideos(false)
    }
  }

  const deleteVideoHandler = async (videoId: number) => {
    if (!turbine) return

    if (!confirm('Bu videoyu silmek istediğinizden emin misiniz?')) return

    try {
      const response = await turbineService.deleteVideo(turbine.id, videoId)

      if (response.success) {
        setExistingVideos(prev => prev.filter(v => v.id !== videoId))
        showNotification('success', 'Video başarıyla silindi')
      } else {
        // Kural 5+6: success:false bir hatadir; backend mesaji korunur.
        showNotification('error', t.errors.title, responseErrorMessage(response, t, 'Video silme başarısız'))
      }
    } catch (error: unknown) {
      logError('TurbineDetail.deleteVideoHandler', error)
      showNotification('error', t.errors.title, toUserMessage(error, t, 'Video silinirken hata oluştu.'))
    }
  }

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent, roomIndex: number) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(roomIndex)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(null)
  }

  const handleDrop = (e: React.DragEvent, bladeIndex: number) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(null)
    
    const files = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.pcd')
    )
    if (files.length > 0) {
      const bladeKey = `blade-${String.fromCharCode(65 + bladeIndex)}` // blade-A, blade-B, blade-C
      handleBladeFileSelect(bladeKey, files)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, bladeIndex: number) => {
    e.stopPropagation()
    const files = Array.from(e.target.files || []).filter(file => 
      file.type.startsWith('image/') || file.name.toLowerCase().endsWith('.pcd')
    )
    if (files.length > 0) {
      const bladeKey = `blade-${String.fromCharCode(65 + bladeIndex)}` // blade-A, blade-B, blade-C
      handleBladeFileSelect(bladeKey, files)
    }
    // Reset the input
    e.target.value = ''
  }

  const handleUploadClick = (e: React.MouseEvent, bladeIndex: number) => {
    e.preventDefault()
    e.stopPropagation()
    
    const target = e.target as HTMLElement
    if (!target.closest('.upload-zone')) {
      return
    }
    
    
    const fileInput = document.getElementById(`file-input-${bladeIndex}`) as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
      fileInput.click()
    }
  }

  // Modal functions
  const openPhotoModal = (roomNumber: number, photoIndex: number) => {
    const roomPhotos = allPhotos.filter(photo => 
      photo.room?.toString() === roomNumber.toString() && 
      (!photo.category || photo.category === null || photo.category === undefined)
    ) || []
    const selectedRoomFiles = selectedFiles[roomNumber] || []
    const combinedPhotos = [...roomPhotos, ...selectedRoomFiles]
    
    setModalPhoto({
      photos: combinedPhotos,
      currentIndex: photoIndex,
      roomNumber
    })
  }

  const closeModal = () => {
    setModalPhoto(null)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 border border-emerald-200 shadow-sm'
      case 'maintenance': return 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200 shadow-sm'
      default: return 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-800 border border-gray-200 shadow-sm'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return '🟢 Aktif'
      case 'maintenance': return '🔧 Bakımda'
      default: return '❓ Bilinmiyor'
    }
  }

  // --- DYNAMIC YEAR GENERATION (THE FIX) ---
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - i); // Current year + 10 past years

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(event.target.value, 10);
    setSelectedYear(year);
  };

  return {
    // --- Çekirdek veri ---
    turbine,
    loading,
    error,
    currentUser,
    isSuperAdmin,
    isCustomer,
    canDeletePhotos,
    allPhotos,
    categoryPhotos,
    photosLoading,
    availableYears,
    filteredPhotosBySearch,
    // --- Yukleme hatalari + "tekrar dene" ---
    photosLoadError,
    retryLoadPhotos,
    reportsLoadError,
    retryLoadReports,
    videosLoadError,
    retryLoadVideos,
    // --- Yükleme (oda/blade/kategori fotoğrafları) ---
    selectedFiles,
    setSelectedFiles,
    selectedBladeFiles,
    uploading,
    uploadError,
    setUploadError,
    dragOver,
    handleFileSelect,
    handleBladeFileSelect,
    removeSelectedBladeFile,
    handleUploadSelectedBlade,
    handleUploadSelected,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleUploadClick,
    // --- Foto modal + silme ---
    modalPhoto,
    setModalPhoto,
    openPhotoModal,
    closeModal,
    deleteModal,
    setDeleteModal,
    closeDeleteModal,
    deleteError,
    setDeleteError,
    isDeletingPhoto,
    handleDeletePhotoFromList,
    handleDeletePhoto,
    confirmDeletePhoto,
    // --- Bulk delete ---
    isBulkDeleteMode,
    toggleBulkDeleteMode,
    selectedPhotosForBulkDelete,
    togglePhotoForBulkDelete,
    handleBulkDelete,
    isDeletingBulk,
    bulkDeleteError,
    setBulkDeleteError,
    // --- Karşılaştırma ---
    isComparisonMode,
    toggleComparisonMode,
    selectedPhotosForComparison,
    togglePhotoSelection,
    openComparisonModal,
    showComparisonModal,
    setShowComparisonModal,
    // --- Arama / filtre ---
    searchTerm,
    setSearchTerm,
    selectedYear,
    setSelectedYear,
    allPhotosSelectedYear,
    setAllPhotosSelectedYear,
    allPhotosSelectedWeek,
    setAllPhotosSelectedWeek,
    selectedPhotoType,
    setSelectedPhotoType,
    weekOptions,
    yearOptions,
    // --- Kategori yıl/blade seçimleri ---
    dronePhotoYear,
    setDronePhotoYear,
    operatorPhotoYear,
    setOperatorPhotoYear,
    robotPhotoYear,
    setRobotPhotoYear,
    selectedDroneBlade,
    setSelectedDroneBlade,
    selectedOperatorBlade,
    setSelectedOperatorBlade,
    selectedRobotBlade,
    setSelectedRobotBlade,
    // --- Dropdown açık/kapalı state'leri + ref'ler ---
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isYearDropdownOpen,
    setIsYearDropdownOpen,
    isAllPhotosYearDropdownOpen,
    setIsAllPhotosYearDropdownOpen,
    isAllPhotosWeekDropdownOpen,
    setIsAllPhotosWeekDropdownOpen,
    isTypeDropdownOpen,
    setIsTypeDropdownOpen,
    isDroneYearDropdownOpen,
    setIsDroneYearDropdownOpen,
    isOperatorYearDropdownOpen,
    setIsOperatorYearDropdownOpen,
    isRobotYearDropdownOpen,
    setIsRobotYearDropdownOpen,
    isDroneBladeDropdownOpen,
    setIsDroneBladeDropdownOpen,
    isOperatorBladeDropdownOpen,
    setIsOperatorBladeDropdownOpen,
    isRobotBladeDropdownOpen,
    setIsRobotBladeDropdownOpen,
    yearDropdownRef,
    allPhotosYearDropdownRef,
    allPhotosWeekDropdownRef,
    typeDropdownRef,
    // --- Blade-oda seçimi ---
    bladeRoomSelection,
    setBladeRoomSelection,
    isBladeRoomDropdownOpen,
    setIsBladeRoomDropdownOpen,
    // --- Raporlar ---
    selectedReports,
    uploadingReports,
    reportUploadError,
    existingReports,
    loadingReports,
    reportYear,
    setReportYear,
    reportWeek,
    setReportWeek,
    isReportYearDropdownOpen,
    setIsReportYearDropdownOpen,
    isReportWeekDropdownOpen,
    setIsReportWeekDropdownOpen,
    reportWeekDropdownRef,
    handleReportSelect,
    removeSelectedReport,
    uploadReports,
    deleteReport,
    cleanFileName,
    // --- Videolar ---
    selectedVideoType,
    setSelectedVideoType,
    selectedVideos,
    uploadingVideos,
    videoUploadError,
    videoYear,
    setVideoYear,
    videoWeek,
    setVideoWeek,
    existingVideos,
    loadingVideos,
    isVideoTypeDropdownOpen,
    setIsVideoTypeDropdownOpen,
    isVideoYearDropdownOpen,
    setIsVideoYearDropdownOpen,
    isVideoWeekDropdownOpen,
    setIsVideoWeekDropdownOpen,
    playingVideoId,
    setPlayingVideoId,
    videoTypeDropdownRef,
    videoYearDropdownRef,
    videoWeekDropdownRef,
    handleVideoSelect,
    removeSelectedVideo,
    uploadVideos,
    deleteVideoHandler,
    // --- Yorumlar ---
    // NOT: `notification` / `hideNotification` KALDIRILDI. Bildirimler artik
    // uygulama capindaki NotificationProvider'dan geciyor; ekranda ayrica
    // NotificationModal render edilirse CIFT toast olurdu.
    isCommentsOpen,
    setIsCommentsOpen,
  }
}
